import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { ERTFireDepartmentBCTurnoverRecord } from "../interfaces/ert-firedepartment-bc-turnover/ert-firedepartment-bc-turnover";
import {
  IFireDepartmentBcTurnover,
  SubCatgory,
  TurnoverCategory,
  TurnoverStatus,
} from "../interfaces/firedepartment-bc-turnover/firedepartmentBcTurnover";
import { Router } from "@angular/router";
import { FireDepartmentsBcTurnoversService } from "../services/firedepartments/fireDepartmentsBcTurnovers.service";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { IPicklistValuesData } from "../interfaces/administration/picklistValuesData";
import { PicklistService } from "../services/administration/picklist/picklist.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";
import { LoggedUserService } from "../services/common/logged-user.service";
import { FilterService } from "../services/common/filter.service";
import { RoleNames } from "../utils/role-names";
import { forkJoin } from "rxjs";

// Interface for filter criteria
interface FilterCriteria {
  column: string;
  operator: string;
  value: any;
}

// New interface for saved filter configurations
interface SavedFilterConfig {
  name: string;
  filters: FilterCriteria[];
  timestamp: number;
}

@Component({
  selector: "ert-firedepartment-bc-turnover",
  templateUrl: "./ert-firedepartment-bc-turnover.component.html",
  styles: [
    `
      .button-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
      }
    `,
  ],
})
export class ErtFiredepartmentBcTurnoverComponent
  implements OnInit, AfterViewInit
{
  title = "Fire Department BC Turnover";
  totalItems: number;
  sortedBy: string;
  searchForm: FormGroup;

  showCarryoverCommentsUpdate: boolean = false;

  displayedColumns: Map<string, string> = new Map([
    ["itemNumber", "ID"],
    ["turnoverCategory", "Category"],
    ["currentComments", "Current Comments"],
    ["numberOfOpenDays", "Number of Open Days"],
    ["crewLastPosted", "Crew Last Posted"],
    ["shiftLastPosted", "Shift Last Posted"],
    ["status", "Status"],
    ["actions", "Actions"],
  ]);

  dataSource = new MatTableDataSource<IFireDepartmentBcTurnover>();
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());
  crewFieldId: number = 0;
  shiftFieldId: number = 0;
  categoryFieldId: number = 0;

  carryoverCommentsSuccess: boolean = false;
  carryoverCommentsUpdateLength: number = 0;

  isLoading: boolean = false;
  isDeletingEnabled: boolean = false;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Filter properties
  activeFilters: FilterCriteria[] = [];
  filterForm: FormGroup;
  savedFilterForm: FormGroup;
  savedFilters: SavedFilterConfig[] = [];
  showSavedFiltersPanel = false;

  // Storage keys
  private readonly ACTIVE_FILTERS_STORAGE_KEY =
    "ert-fire-department-active-filters";
  private readonly SAVED_FILTERS_STORAGE_KEY =
    "ert-fire-department-saved-filters";

  availableOperators = [
    { value: "contains", label: "Contains" },
    { value: "equals", label: "Equals" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "greaterThan", label: "Greater than" },
    { value: "lessThan", label: "Less than" },
  ];
  showFilterPanel = false;

  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50];
  paginationInfo = "";
  currentUser: ILoggedUserData;

  // Storage key for filters
  readonly bcturnover_filters_storage_key =
    "ert-fire-department-bc-turnover-filters";

  constructor(
    private router: Router,
    private service: FireDepartmentsBcTurnoversService,
    private pickListsService: PicklistService,
    private fb: FormBuilder,
    private loggedUserService: LoggedUserService,
    private filterService: FilterService
  ) {
    this.totalItems = 0;
    this.sortedBy = "Not sorted";

    // Create and initialize the search form using FormBuilder
    this.searchForm = this.fb.group({
      search: [""],
      category: ["-1"],
    });

    // Initialize filter form
    this.filterForm = this.fb.group({
      column: ["itemNumber"],
      operator: ["contains"],
      value: [""],
    });

    // Initialize saved filter form
    this.savedFilterForm = this.fb.group({
      filterName: ["", []],
    });
  }

  ngOnInit() {
    this.isLoading = true;

    // Configure sorting data accessor for the table
    this.dataSource.sortingDataAccessor = (
      data: IFireDepartmentBcTurnover,
      sortHeaderId: string
    ) => {
      switch (sortHeaderId) {
        case "itemNumber":
          return data.id;
        case "turnoverCategory":
          return data.category || "";
        case "currentComments":
          return data.comments || "";
        case "numberOfOpenDays":
          return this.getNumberOfOpenDays(data);
        case "crewLastPosted":
          return data.crew || "";
        case "shiftLastPosted":
          return data.shift || "";
        case "status":
          return this.getStatusName(data.status) || "";
        default:
          return (data as any)[sortHeaderId];
      }
    };

    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.isDeletingEnabled =
          user.role === RoleNames.ERT_ADMINISTRATOR ? true : false;

        forkJoin({
          crew: this.pickListsService.getFieldIdByName("Crew"),
          shift: this.pickListsService.getFieldIdByName("Shift"),
          category: this.pickListsService.getFieldIdByName("Category"),
        }).subscribe((results) => {
          if (results.crew) this.crewFieldId = results.crew;
          if (results.shift) this.shiftFieldId = results.shift;
          if (results.category) this.categoryFieldId = results.category;

          this.loadData();
        });

        // Load saved filters from localStorage
        this.loadSavedFilters();

        // Load active filters from localStorage
        this.loadActiveFilters();

        // Initialize filter service with storage keys
        this.filterService.loadActiveFiltersFromStorage(
          this.bcturnover_filters_storage_key
        );
        this.filterService.loadSavedFiltersFromStorage(
          this.SAVED_FILTERS_STORAGE_KEY
        );

        // Subscribe to active filters changes
        this.filterService
          .getActiveFilters(this.bcturnover_filters_storage_key)
          .subscribe((filters) => {
            this.activeFilters = filters;
            this.search(); // Apply filters whenever they change
          });
      }
    });
  }

  // Load any active filters from localStorage
  loadActiveFilters(): void {
    try {
      const storedFilters = localStorage.getItem(
        this.ACTIVE_FILTERS_STORAGE_KEY
      );
      if (storedFilters) {
        this.activeFilters = JSON.parse(storedFilters);

        if (this.activeFilters.length > 0) {
          setTimeout(() => {
            this.search();
          }, 500);
        }
      }
    } catch (error) {
      console.error("Error loading active filters from localStorage:", error);
      this.activeFilters = [];
    }
  }

  // Save active filters to localStorage
  saveActiveFilters(): void {
    try {
      localStorage.setItem(
        this.ACTIVE_FILTERS_STORAGE_KEY,
        JSON.stringify(this.activeFilters)
      );
    } catch (error) {
      console.error("Error saving active filters to localStorage:", error);
    }
  }

  // Load saved filter configurations from localStorage
  loadSavedFilters(): void {
    try {
      const storedSavedFilters = localStorage.getItem(
        this.SAVED_FILTERS_STORAGE_KEY
      );
      if (storedSavedFilters) {
        this.savedFilters = JSON.parse(storedSavedFilters);
      }
    } catch (error) {
      console.error("Error loading saved filters from localStorage:", error);
      this.savedFilters = [];
    }
  }

  // Save filter configurations to localStorage
  saveSavedFilters(): void {
    try {
      localStorage.setItem(
        this.SAVED_FILTERS_STORAGE_KEY,
        JSON.stringify(this.savedFilters)
      );
    } catch (error) {
      console.error(
        "Error saving filter configurations to localStorage:",
        error
      );
    }
  }

  // Save current filter configuration with a name
  saveCurrentFilters(): void {
    const filterName = this.savedFilterForm.get("filterName")?.value;
    if (!filterName) {
      alert("Please enter a name for this filter configuration");
      return;
    }

    // Check if name already exists
    const existingIndex = this.savedFilters.findIndex(
      (f) => f.name === filterName
    );
    if (existingIndex >= 0) {
      if (
        !confirm(
          `A filter configuration named "${filterName}" already exists. Do you want to replace it?`
        )
      ) {
        return;
      }
      // Remove existing config with the same name
      this.savedFilters.splice(existingIndex, 1);
    }

    // Create a new saved filter configuration
    const newSavedFilter: SavedFilterConfig = {
      name: filterName,
      filters: [...this.activeFilters], // clone the array
      timestamp: Date.now(),
    };

    this.savedFilters.push(newSavedFilter);
    this.saveSavedFilters();

    // Reset form
    this.savedFilterForm.patchValue({ filterName: "" });
  }

  // Load a saved filter configuration
  loadFilterConfig(config: SavedFilterConfig): void {
    this.activeFilters = [...config.filters]; // clone the array
    this.saveActiveFilters();
    this.search();
  }

  // Delete a saved filter configuration
  deleteFilterConfig(index: number): void {
    if (confirm("Are you sure you want to delete this filter configuration?")) {
      this.savedFilters.splice(index, 1);
      this.saveSavedFilters();
    }
  }

  // Toggle saved filters panel
  toggleSavedFiltersPanel(): void {
    this.showSavedFiltersPanel = !this.showSavedFiltersPanel;
  }

  editTurnover(id: number) {
    this.router.navigate(["/firedepartment-bc-turnover/edit", id]);
  }

  confirmDeleteTurnover(entity: IFireDepartmentBcTurnover) {
    if (confirm("Are you sure you want to delete this record?")) {
      this.service.deleteFireDepartmentBcTurnover(entity).subscribe((data) => {
        this.loadData();
      });
    }
  }

  loadData() {
    this.service
      .getFireDepartmentsBcTurnovers(Number(this.currentUser.facilityId))
      .subscribe((data) => {
        if (data) {
          this.dataSource.data = data;
          this.totalItems = this.dataSource.data.length;
          data.forEach((element) => {
            const latestCarryoverComment = element.carryoverComments?.length
              ? [...element.carryoverComments].sort((a, b) => b.id - a.id)[0]
              : null;

            // Use the latest comment's picklistValues if available, otherwise fall back to element's values
            element.crew = latestCarryoverComment?.picklistValues
              ? this.getPicklistValue(
                  latestCarryoverComment.picklistValues,
                  "crew"
                )
              : this.getPicklistValue(element.selectedValuesInTurnover, "crew");

            element.shift = latestCarryoverComment?.picklistValues
              ? this.getPicklistValue(
                  latestCarryoverComment.picklistValues,
                  "shift"
                )
              : this.getPicklistValue(
                  element.selectedValuesInTurnover,
                  "shift"
                );
            element.comments =
              latestCarryoverComment && latestCarryoverComment?.comments !== ""
                ? latestCarryoverComment?.comments
                : element.initialComments;
            element.category = this.getPicklistValue(
              element.selectedValuesInTurnover,
              "category"
            );
          });
          setTimeout(() => {
            this.dataSource.sort = this.sort;
            this.dataSource.paginator = this.paginator;

            // Set initial sort to Category (asc) instead of itemNumber (desc)
            this.sort.sort({
              id: "turnoverCategory",
              start: "asc",
            } as MatSortable);

            this.sort.sortChange.subscribe((sortState: Sort) => {
              this.onSortChange(sortState);
            });

            // Update the sorted by text to reflect the new default sort
            this.sortedBy = `Sorted by ${this.displayedColumns.get(
              "turnoverCategory"
            )} (asc)`;
            this.updatePaginationInfo();
          }, 10);
        }
        this.isLoading = false;
      });
  }

  ngAfterViewInit() {}

  search() {
    const searchTerm =
      this.searchForm.get("search")?.value?.trim().toLowerCase() || "";
    const category = this.searchForm.get("category")?.value || "";

    this.dataSource.filterPredicate = (data, filter) => {
      // Basic search in comments
      const searchMatch = data.initialComments
        ? data.initialComments.toLowerCase().includes(searchTerm)
        : searchTerm === "";

      // Category match in selectedValuesInTurnover
      let categoryMatch = true;
      if (category && category !== "-1") {
        categoryMatch =
          data.selectedValuesInTurnover?.some(
            (item) =>
              item.picklistFieldId === this.categoryFieldId &&
              item.id.toString() === category
          ) || false;
      }

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (this.activeFilters.length > 0) {
        advancedFiltersMatch = this.activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && categoryMatch && advancedFiltersMatch;
    };

    this.dataSource.filter =
      searchTerm + category + JSON.stringify(this.activeFilters);
    this.totalItems = this.dataSource.filteredData.length;

    // Reset to first page when filtering
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    // Update pagination info after filter
    this.updatePaginationInfo();
  }

  // Helper to get property value for filtering
  getPropertyValue(data: any, property: string): any {
    switch (property) {
      case "itemNumber":
        return data.id;
      case "turnoverCategory":
        return data.category;
      case "currentComments":
        return data.initialComments;
      case "numberOfOpenDays":
        return this.getNumberOfOpenDays(data);
      case "crewLastPosted":
        return data.crew;
      case "shiftLastPosted":
        return data.shift;
      case "status":
        return this.getStatusName(data.status);
      default:
        return "";
    }
  }

  // Add a new filter to the active filters
  addFilter() {
    const filterCriteria: FilterCriteria = {
      column: this.filterForm.get("column")?.value,
      operator: this.filterForm.get("operator")?.value,
      value: this.filterForm.get("value")?.value,
    };

    this.activeFilters.push(filterCriteria);
    this.saveActiveFilters(); // Save to localStorage
    this.search();

    // Reset filter form value but keep column/operator
    this.filterForm.patchValue({ value: "" });
  }

  // Remove a filter from the active filters
  removeFilter(index: number) {
    this.activeFilters.splice(index, 1);
    this.saveActiveFilters(); // Save to localStorage
    this.search();
  }

  // Clear all filters
  clearAllFilters() {
    this.filterService.clearAllFilters(this.bcturnover_filters_storage_key);
    this.activeFilters = [];
    this.saveActiveFilters(); // Save to localStorage
    this.searchForm.patchValue({
      search: "",
      category: "-1",
    });
    this.search();
  }

  // Toggle filter panel
  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  // Handler for the filtersChanged event from advanced-filter-panel component
  onFiltersChanged(filters: FilterCriteria[]): void {
    this.search();
    this.updatePaginationInfo();
  }

  // Get column display name for UI
  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  onCategorySelected(item: any): void {
    this.search();
  }

  onSortChange(sortState: Sort) {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${
        this.displayedColumns.get(sortState.active) || sortState.active
      } (${sortState.direction})`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  getNumberOfOpenDays(element: IFireDepartmentBcTurnover): number {
    const turnoverDate = new Date(element.turnoverDate);
    let endDate: Date;

    if (element.status === TurnoverStatus.Resolved && element.resolvedDate) {
      endDate = new Date(element.resolvedDate);
    } else {
      endDate = new Date();
    }

    const timeDiff = Math.abs(endDate.getTime() - turnoverDate.getTime());
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  getSubCategoryName(subCategory?: SubCatgory): string {
    if (subCategory === undefined || subCategory === null) {
      return "";
    }
    return SubCatgory[subCategory];
  }

  getStatusName(status?: TurnoverStatus): string {
    if (status === undefined || status === null) {
      return "";
    }
    return TurnoverStatus[status];
  }

  getPicklistValue(
    picklistValues: IPicklistValuesData[],
    fieldType: "category" | "crew" | "shift"
  ): string {
    let fieldId: number;

    switch (fieldType) {
      case "category":
        fieldId = this.categoryFieldId;
        break;
      case "crew":
        fieldId = this.crewFieldId;
        break;
      case "shift":
        fieldId = this.shiftFieldId;
        break;
    }

    return this.service.getValueForField(picklistValues, fieldId);
  }

  addRecord() {
    this.router.navigate(["/firedepartment-bc-turnover/create"]);
  }

  carryoverComments() {
    this.showCarryoverCommentsUpdate = !this.showCarryoverCommentsUpdate;
  }

  // Add this to your component where carryoverCommentsSuccess is set to true
  setCarryoverCommentsSuccess(value: boolean): void {
    this.carryoverCommentsSuccess = value;

    if (value === true) {
      // Auto dismiss after 5 seconds
      setTimeout(() => {
        this.carryoverCommentsSuccess = false;
      }, 5000);
    }
  }

  handleSaved(data: number): void {
    this.isLoading = true;
    this.carryoverCommentsUpdateLength = data;
    this.setCarryoverCommentsSuccess(true);
    this.loadData();
    this.carryoverComments();
  }

  handleCancelled(): void {
    this.carryoverComments();
  }

  // Add pagination info update method
  updatePaginationInfo(): void {
    if (this.paginator) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize + 1;
      const endIndex = Math.min(
        (this.paginator.pageIndex + 1) * this.paginator.pageSize,
        this.totalItems
      );
      this.paginationInfo = `Showing ${startIndex} to ${endIndex} of ${this.totalItems}`;
    } else {
      this.paginationInfo = "";
    }
  }

  // Page change handler
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.updatePaginationInfo();
  }
}
